import { formatPrice } from '@retainerclub/common';
import PropTypes from 'prop-types';
import {
  cadenceText,
  perSetCents,
  totalPriceLine,
} from './helpers/acknowledgmentCopy';

// The tier cards, one full-width row each: name and description on the left,
// pricing on the right (stacked on phones). The per-set price is the
// headline; the line beneath it states what is actually charged each renewal,
// and the agreement statement repeats that amount. A tier that reports no
// sets falls back to the renewal price as its headline.
function SubscriptionDisclosureTiers({
  tiers,
  defaultTierIds,
  selectedTierId = null,
  allowSelection = false,
  onSelect = null,
  locale = 'en-US',
  defaultBadge = 'Recommended',
}) {
  return (
    <div className="flex flex-col gap-4">
      {tiers.map((tier) => {
        const productId = tier.product.id;
        const isSelected = selectedTierId === productId;
        const isDefault = !selectedTierId && defaultTierIds.includes(productId);
        const highlighted = isSelected || isDefault;
        const badge = isSelected ? 'Selected' : isDefault ? defaultBadge : null;
        const months = tier.product.renewalIntervalMonths;
        const perSet = perSetCents(tier.price, tier.units);

        const content = (
          <>
            {badge && (
              <span className="mb-3 flex justify-end">
                <span className="rounded-full bg-rcprimary-400 px-3 py-1 font-semibold text-gray-950 text-xs uppercase tracking-wide">
                  {badge}
                </span>
              </span>
            )}
            <span className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
              <span className="block min-w-0">
                <span className="block font-bold text-2xl text-gray-950">
                  {tier.product.name}
                </span>
                {tier.product.description && (
                  <span className="mt-1 block text-gray-700 text-sm">
                    {tier.product.description}
                  </span>
                )}
              </span>
              <span className="block shrink-0 sm:text-right">
                <span className="block font-bold text-3xl text-gray-950 tracking-tight">
                  {formatPrice(perSet ?? tier.price, locale)}{' '}
                  <span className="font-normal text-gray-500 text-sm">
                    {perSet === null ? cadenceText(months) : 'per set'}
                  </span>
                </span>
                <span className="mt-1 block text-gray-600 text-xs">
                  {totalPriceLine({
                    priceCents: tier.price,
                    cadenceMonths: months,
                    locale,
                  })}
                </span>
              </span>
            </span>
          </>
        );

        const cardClass = `block w-full rounded-2xl bg-white p-6 text-left ${
          highlighted
            ? 'shadow-xl ring-2 ring-rcprimary-400'
            : 'ring-1 ring-gray-200'
        }`;

        if (allowSelection) {
          return (
            <button
              key={productId}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelect?.(productId)}
              className={`${cardClass} cursor-pointer transition hover:ring-rcprimary-300 focus-visible:outline-2 focus-visible:outline-rcprimary-400 focus-visible:outline-offset-2`}
            >
              {content}
            </button>
          );
        }

        return (
          <div key={productId} className={cardClass}>
            {content}
          </div>
        );
      })}
    </div>
  );
}

SubscriptionDisclosureTiers.propTypes = {
  tiers: PropTypes.array.isRequired,
  defaultTierIds: PropTypes.array.isRequired,
  selectedTierId: PropTypes.string,
  allowSelection: PropTypes.bool,
  onSelect: PropTypes.func,
  locale: PropTypes.string,
  defaultBadge: PropTypes.string,
};

export default SubscriptionDisclosureTiers;
